import { useRef, useState } from "react";
import { ScrollView, View, useWindowDimensions } from "react-native";
import { KeyboardAvoidingView, KeyboardAwareScrollView } from "react-native-keyboard-controller";
import { useTranslation } from "react-i18next";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { useTheme } from "../../../hooks/useTheme";
import { BottomSheet, BottomSheetScrollView } from "../../onboarding/BottomSheet";
import { CVDataProvider, useCVData } from "../../../lib/cv-mock-data";
import { ControlCopy, ControlRow, ControlSection } from "./DataControlParts";
import {
  useProfile,
  useUpdateProfile,
  useUpsertExperience,
  useDeleteExperience,
  useUpsertEducation,
  useDeleteEducation,
  useUpsertCertification,
  useDeleteCertification,
  useUpsertHighlight,
  useDeleteHighlight,
  useSaveSkillGroups,
  useSaveLanguages,
} from "../../../hooks/useProfileApi";
import {
  mapExperienceToAPI,
  mapEducationToAPI,
  mapCertificationToAPI,
  mapHighlightToAPI,
  mapLanguagesToAPI,
} from "../../../lib/cv-mapping";
import { EditHeaderForm } from "../../cv/forms/EditHeaderForm";
import { EditSummaryForm } from "../../cv/forms/EditSummaryForm";
import { EditExperienceForm } from "../../cv/forms/EditExperienceForm";
import { EditEducationForm } from "../../cv/forms/EditEducationForm";
import { EditCertificationsForm } from "../../cv/forms/EditCertificationsForm";
import { EditHighlightsForm } from "../../cv/forms/EditHighlightsForm";
import { EditSkillsForm } from "../../cv/forms/EditSkillsForm";
import { EditLanguagesForm } from "../../cv/forms/EditLanguagesForm";

export const MAIN_CV_SECTIONS = [
  "header",
  "summary",
  "experience",
  "education",
  "certifications",
  "highlights",
  "skills",
  "languages",
] as const;
export type CvManagedSection = (typeof MAIN_CV_SECTIONS)[number];

// Each *SectionBody below reuses, verbatim, the same handleSave/mutation
// wiring as its full-screen counterpart in app/(app)/cv/edit-*.tsx. Only the
// host (this bottom sheet instead of a pushed `/cv` route) and `closeOnSave`
// differ — there's no route here for the form to `router.back()` into, so the
// sheet decides where a save lands, via `onSaved`: back on the section list,
// the same place the header's back button leads.

function HeaderSectionBody({ onSaved }: { onSaved: () => void }) {
  const { theme } = useTheme();
  const { profileRef } = useCVData();
  const updateProfile = useUpdateProfile();
  const handleSave = async () => {
    const p = profileRef.current;
    await updateProfile.mutateAsync({
      name: p.name,
      headline: p.headline,
      location: p.location,
      contactEmail: p.contactEmail,
      phone: p.phone,
      avatarUrl: p.avatarUrl || null,
      linkedin: p.linkedin,
      website: p.website,
      github: p.github,
      dribbble: p.dribbble,
      behance: p.behance,
    });
  };
  return (
    <KeyboardAwareScrollView
      style={{ flex: 1, backgroundColor: theme.background }}
      contentContainerStyle={{ paddingBottom: 48 }}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="on-drag"
      bottomOffset={24}
    >
      <EditHeaderForm
        closeOnSave={false}
        onSave={async () => {
          await handleSave();
          onSaved();
        }}
      />
    </KeyboardAwareScrollView>
  );
}

function SummarySectionBody({ onSaved }: { onSaved: () => void }) {
  const { theme } = useTheme();
  const { profileRef } = useCVData();
  const updateProfile = useUpdateProfile();
  const handleSave = async () => {
    const p = profileRef.current;
    await updateProfile.mutateAsync({ headline: p.headline, summary: p.summary });
  };
  return (
    <KeyboardAvoidingView
      style={{ flex: 1, backgroundColor: theme.background }}
      behavior="padding"
      automaticOffset
    >
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: 48 }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <EditSummaryForm
          closeOnSave={false}
          onSave={async () => {
            await handleSave();
            onSaved();
          }}
        />
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

function ExperienceSectionBody({ onSaved }: { onSaved: () => void }) {
  const { theme } = useTheme();
  const { profile, profileRef } = useCVData();
  const upsertExp = useUpsertExperience();
  const deleteExp = useDeleteExperience();
  const initialIdsRef = useRef(new Set(profile.experience.map((e) => e.id)));
  const handleSave = async () => {
    const items = profileRef.current.experience;
    const currentIds = new Set(items.map((e) => e.id));
    const removedIds = [...initialIdsRef.current].filter((id) => !currentIds.has(id));
    const results = await Promise.allSettled([
      ...items.map((exp, i) => upsertExp.mutateAsync(mapExperienceToAPI(exp, i))),
      ...removedIds.map((id) => deleteExp.mutateAsync(id)),
    ]);
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed > 0 && failed === results.length) {
      throw new Error("Failed to save");
    }
  };
  return (
    <KeyboardAvoidingView
      style={{ flex: 1, backgroundColor: theme.background }}
      behavior="padding"
      automaticOffset
    >
      <EditExperienceForm
        closeOnSave={false}
        onSave={async () => {
          await handleSave();
          onSaved();
        }}
      />
    </KeyboardAvoidingView>
  );
}

function EducationSectionBody({ onSaved }: { onSaved: () => void }) {
  const { theme } = useTheme();
  const { profile, profileRef } = useCVData();
  const upsertEdu = useUpsertEducation();
  const deleteEdu = useDeleteEducation();
  const initialIdsRef = useRef(new Set(profile.education.map((e) => e.id)));
  const handleSave = async () => {
    const items = profileRef.current.education;
    const currentIds = new Set(items.map((e) => e.id));
    const removedIds = [...initialIdsRef.current].filter((id) => !currentIds.has(id));
    const results = await Promise.allSettled([
      ...items.map((edu, i) => upsertEdu.mutateAsync(mapEducationToAPI(edu, i))),
      ...removedIds.map((id) => deleteEdu.mutateAsync(id)),
    ]);
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed > 0 && failed === results.length) {
      throw new Error("Failed to save");
    }
  };
  return (
    <KeyboardAvoidingView
      style={{ flex: 1, backgroundColor: theme.background }}
      behavior="padding"
      automaticOffset
    >
      <EditEducationForm
        closeOnSave={false}
        onSave={async () => {
          await handleSave();
          onSaved();
        }}
      />
    </KeyboardAvoidingView>
  );
}

function CertificationsSectionBody({ onSaved }: { onSaved: () => void }) {
  const { theme } = useTheme();
  const { profile, profileRef } = useCVData();
  const upsertCert = useUpsertCertification();
  const deleteCert = useDeleteCertification();
  const initialIdsRef = useRef(new Set(profile.certifications.map((c) => c.id)));
  const handleSave = async () => {
    const items = profileRef.current.certifications;
    const currentIds = new Set(items.map((c) => c.id));
    const removedIds = [...initialIdsRef.current].filter((id) => !currentIds.has(id));
    const results = await Promise.allSettled([
      ...items.map((cert, i) => upsertCert.mutateAsync(mapCertificationToAPI(cert, i))),
      ...removedIds.map((id) => deleteCert.mutateAsync(id)),
    ]);
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed > 0 && failed === results.length) {
      throw new Error("Failed to save");
    }
  };
  return (
    <View
      style={{ flex: 1, backgroundColor: theme.background }}
    >
      <EditCertificationsForm
        closeOnSave={false}
        onSave={async () => {
          await handleSave();
          onSaved();
        }}
      />
    </View>
  );
}

function HighlightsSectionBody({ onSaved }: { onSaved: () => void }) {
  const { theme } = useTheme();
  const { profile, profileRef } = useCVData();
  const upsertHL = useUpsertHighlight();
  const deleteHL = useDeleteHighlight();
  const initialIdsRef = useRef(new Set(profile.highlights.filter((h) => h.id).map((h) => h.id!)));
  const handleSave = async () => {
    const items = profileRef.current.highlights;
    const currentIds = new Set(items.filter((h) => h.id).map((h) => h.id!));
    const removedIds = [...initialIdsRef.current].filter((id) => !currentIds.has(id));
    const results = await Promise.allSettled([
      ...items.map((hl, i) => upsertHL.mutateAsync(mapHighlightToAPI(hl, i))),
      ...removedIds.map((id) => deleteHL.mutateAsync(id)),
    ]);
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed > 0 && failed === results.length) {
      throw new Error("Failed to save");
    }
  };
  return (
    <View style={{ flex: 1, backgroundColor: theme.background }}>
      <EditHighlightsForm
        closeOnSave={false}
        onSave={async () => {
          await handleSave();
          onSaved();
        }}
      />
    </View>
  );
}

function SkillsSectionBody({ onSaved }: { onSaved: () => void }) {
  const { theme } = useTheme();
  const { profileRef } = useCVData();
  const saveSkills = useSaveSkillGroups();
  const handleSave = async () => {
    await saveSkills.mutateAsync(profileRef.current.skillGroups);
  };
  return (
    <KeyboardAwareScrollView
      style={{ flex: 1, backgroundColor: theme.background }}
      contentContainerStyle={{ paddingBottom: 48 }}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="on-drag"
      bottomOffset={24}
    >
      <EditSkillsForm
        closeOnSave={false}
        onSave={async () => {
          await handleSave();
          onSaved();
        }}
      />
    </KeyboardAwareScrollView>
  );
}

function LanguagesSectionBody({ onSaved }: { onSaved: () => void }) {
  const { theme } = useTheme();
  const saveLangs = useSaveLanguages();
  const handleSave = async (languages: string[]) => {
    await saveLangs.mutateAsync(mapLanguagesToAPI(languages));
  };
  return (
    <View
      style={{ flex: 1, backgroundColor: theme.background }}
    >
      <EditLanguagesForm
        closeOnSave={false}
        onSave={async (languages) => {
          await handleSave(languages);
          onSaved();
        }}
      />
    </View>
  );
}

const SECTION_BODIES: Record<CvManagedSection, React.ComponentType<{ onSaved: () => void }>> = {
  header: HeaderSectionBody,
  summary: SummarySectionBody,
  experience: ExperienceSectionBody,
  education: EducationSectionBody,
  certifications: CertificationsSectionBody,
  highlights: HighlightsSectionBody,
  skills: SkillsSectionBody,
  languages: LanguagesSectionBody,
};

/**
 * The Settings-only host for main-CV data: the section list and the section
 * editors, in ONE bottom sheet that swaps its own content.
 *
 * Deliberately its own sheet rather than the shared SettingsDetailSurface,
 * which AgentAccessPanel and CvPrivacyControls also render: the editors below
 * are the same forms the `/cv` routes use, so the Settings-specific host is
 * where Settings-specific sizing and gesture setup belong, and neither the
 * shared surface nor the forms have to change to accommodate them.
 *
 * One sheet, not two. Reaching an editor by opening a second sheet over this
 * one cannot work on iOS: a view controller that is already presenting cannot
 * present again, so the second sheet is silently dropped (the rows read as
 * dead, or the editor turns up behind the list on a later open). Dismissing
 * this sheet first and presenting the editor after costs ~640ms of slide-out,
 * settle and slide-in each way. Swapping content in place presents nothing at
 * all, so neither failure has anywhere to happen.
 */
export function DataManagementMainCvSheet({
  visible,
  onClose,
}: {
  visible: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation("common");
  const { height: screenHeight } = useWindowDimensions();
  const [section, setSection] = useState<CvManagedSection | null>(null);
  const editing = section !== null;
  // Enabled for the whole sheet, not just while editing: a save invalidates
  // ['profile'], and only an active observer refetches on invalidation. Kept
  // active through the section list, the fresh read is already in flight (and
  // usually landed) by the time the next section mounts its form.
  const { data: profileData } = useProfile(visible, "master");
  const SectionBody = section ? SECTION_BODIES[section] : null;

  return (
    <BottomSheet
      visible={visible}
      title={
        section
          ? t(`settings.dataManagement.sections.${section}`)
          : t("settings.dataManagement.manageMainCv")
      }
      onClose={onClose}
      // The header steps back to the list while editing; the backdrop and
      // swipe-down keep dismissing the sheet outright.
      headerAction={editing ? "back" : "close"}
      onHeaderAction={editing ? () => setSection(null) : undefined}
      // Reset once the sheet is gone, so an editor stays put through the
      // slide-out instead of flashing back to the list on the way down.
      onAfterClose={() => setSection(null)}
      propagateSwipe
    >
      {SectionBody ? (
        // GestureHandlerRootView: the editors render a drag-to-reorder list,
        // and react-native-gesture-handler throws unless a GestureDetector has
        // one as a React ancestor. The app's only one comes from the drawer, in
        // `(app)`; Settings is a sibling root-Stack route with none, so this
        // sheet hosts its own, the way PhotoCropModal does.
        //
        // Definite height: BottomSheet's sheet is auto-height (maxHeight
        // only) and every SectionBody above is `flex: 1`, which in an
        // auto-height column is flexBasis 0 with no free space to grow into —
        // the editor collapses to a sliver. flexShrink keeps the sheet's own
        // maxHeight able to clamp this back down, so overflow still resolves
        // to the editor's own scroll container rather than a clipped sheet.
        <GestureHandlerRootView
          style={{ height: screenHeight * 0.78, flexShrink: 1 }}
        >
          <CVDataProvider
            key={section}
            initialProfile={profileData}
            target="master"
            isDelegated={false}
          >
            {/* A save steps back to the section list rather than
                dismissing: editing one section is rarely the whole errand,
                and the header's back button already goes there. The form's
                own success toast reports the save. */}
            <SectionBody onSaved={() => setSection(null)} />
          </CVDataProvider>
        </GestureHandlerRootView>
      ) : (
        <BottomSheetScrollView
          style={{ flexShrink: 1 }}
          contentContainerStyle={{ paddingBottom: 16, gap: 20 }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <ControlCopy>
            {t("settings.dataManagement.mainCvDescription")}
          </ControlCopy>
          <ControlSection title={t("settings.controls.chooseSection")}>
            {MAIN_CV_SECTIONS.map((entry) => (
              <ControlRow
                key={entry}
                title={t(`settings.dataManagement.sections.${entry}`)}
                onPress={() => setSection(entry)}
              />
            ))}
          </ControlSection>
        </BottomSheetScrollView>
      )}
    </BottomSheet>
  );
}
